import { Box, Button, Stack } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetPatientByIdQuery } from "src/api/services/patientsApi";
import { useGetNextTaskForPatientQuery } from "src/api/services/tasksApi";
import ClinicalNote from "src/components/ClinicalNote";
import MasonryLayout from "src/components/MasonryLayout";
import MedicalHistory from "src/components/MedicalHistory";
import Messaging from "src/components/Messaging";
import PatientDemographics from "src/components/PatientDemographics";
import PatientIntakes from "src/components/PatientIntakes";
import PatientSubscriptions from "src/components/PatientSubscriptions";
import { MatchParam } from "src/constants/matches";
import PatientOrdersModalProvider from "src/context/PatientOrdersModalProvider";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { useTasks } from "src/context/TasksProvider";
import { useWaitingRoom } from "src/context/WaitingRoomProvider/useWaitingRoom";
import { capitalize } from "src/utils/capitalize";
import PatientInformation from "../NonProviderTaskView/components/PatientInformation";
import PatientOrders from "src/components/PatientOrders";
import TaskView from "./components/TaskView";
import MentalHealthScoreChip from "src/components/MentalHealthScoreChip";
import PatientCoupons from "src/components/PatientCoupons";
import TaskHistory from "src/components/TaskHistory";
import SubscriptionModalProvider from "src/context/SubscriptionModalProvider";
import PatientPayments from "src/components/PatientPayments";
import PatientInsurance from "../PatientInsurance";

const ProviderTaskView = () => {
  const { completeAndNext, getNextTask } = useTasks();
  const patientId = useWaitingRoom((w) => w.patient_id);
  const completed = useWaitingRoom((w) => w.completed_at != null);
  const { data, isLoading: loadingPatient } = useGetPatientByIdQuery(
    patientId ? Number(patientId) : skipToken,
  );
  const waitRoomId = useWaitingRoom((w) => w.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const { queue } = useParams<MatchParam<"QUEUE_TYPE">>();
  const providerId = useClinician((p) => p.id);
  const { data: task, isLoading: loadingTask } = useGetNextTaskForPatientQuery(
    patientId && queue
      ? { patientId, queue: capitalize(queue), providerId }
      : skipToken,
  );

  const handleNext = useCallback(() => {
    if (completed === true) {
      getNextTask();
    } else {
      completeAndNext(waitRoomId);
    }
  }, [completeAndNext, completed, getNextTask, waitRoomId]);

  const showNext = useMemo(() => {
    return loadingTask === false && task == null;
  }, [loadingTask, task]);

  useEffect(() => {
    if (task?.id != null) {
      searchParams.set("task", task.id.toString());
      searchParams.set("patientId", task.patient_id.toString());
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, task?.id, task?.patient_id]);

  if (data == null) return null;

  return (
    <Stack width="100%" gap={3} height={window.innerHeight - 24 - 8}>
      <Stack direction="row" gap={2}>
        <PatientDemographics patient={data} />
        {showNext ? (
          <Button variant="contained" onClick={handleNext} sx={{ width: 150 }}>
            Next Patient
          </Button>
        ) : null}
      </Stack>
      <MentalHealthScoreChip patientId={patientId} />
      <Box overflow="scroll" padding={1}>
        <MasonryLayout>
          <PatientInformation patient={data} loading={loadingPatient} />

          {task ? (
            <TaskView task={task} patient={data} />
          ) : (
            <Messaging allowMessaging={true} patientId={patientId} />
          )}

          <PatientIntakes patientId={patientId} patientStatus={data.status} />
          {queue === "coordinator" ? (
            <PatientCoupons patientId={patientId} />
          ) : null}

          {queue === "coordinator" ? (
            <SubscriptionModalProvider
              patientId={data.id}
              patientGender={data.profile.gender}
            >
              <PatientSubscriptions
                patientId={patientId}
                allowActions={queue === "coordinator"}
              />
            </SubscriptionModalProvider>
          ) : null}
          <PatientOrdersModalProvider>
            <PatientOrders
              patient={data}
              allowActions={queue === "coordinator"}
            />
          </PatientOrdersModalProvider>
          <MedicalHistory patient={data} />
          <PatientInsurance patientProfileId={data.profile_id} />
          <Stack gap={1}>
            <ClinicalNote
              patient={data}
              noteType={"Provider"}
              order={queue === "provider" ? 1 : 2}
              noteTitle="PROVIDER"
            />
            <ClinicalNote
              patient={data}
              noteType={"Coordinator"}
              order={queue === "coordinator" ? 1 : 2}
              noteTitle="HEALTH NAVIGATOR"
            />
          </Stack>
          {queue === "coordinator" ? <TaskHistory patientId={data.id} /> : null}
          {queue === "coordinator" ? <PatientPayments patient={data} /> : null}
        </MasonryLayout>
      </Box>
    </Stack>
  );
};

export default ProviderTaskView;
